import React, { useCallback, useEffect } from 'react';
import { Card } from '../Card';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import { FormTextarea } from 'components/stepper/field-components';
import FieldLabel from 'components/stepper/field-components/FieldLabel';
import Dropzone from 'components/Dropzone';
import { FileItem, FileItemType } from '../complete-my-visit-summary/FileItem';
import { allowedTypes } from './useSummariForm';
import LoadingSummary from '../complete-my-visit-summary/LoadingSummary';
import { useFormContext, useWatch } from 'react-hook-form';
import ModalComponent from 'components/modal/modalComponent';
import { RadioGroup } from 'components/stepper/field-components/FormRadioGroup';
import { twMerge } from 'tailwind-merge';
import ButtonComponent from 'components/button/buttonComponent';
import { Checkbox } from 'components/stepper/field-components/FormCheckboxGroup';
import StorageService from 'utilities/storageService';
import { GetAppointmentOutput } from 'graphql/generated/hasura';
import moment from 'moment';
import Alert from '../../../../components/alertComponent';

const MAX_TEXT_AREA_LENGTH = 10000;
const SOAP_STORAGE = 'soap';
const storage = new StorageService(SOAP_STORAGE, sessionStorage);

interface SummaryEditStateProps {
  fileError?: string | null;
  handleImageUpload: (files: File[]) => Promise<void>;
  handleDeleteFile: (index: number) => void;
  isFileLimitOver: boolean;
  fileLoading: boolean;
  hasPolingLoadData: boolean;
  appointment?: GetAppointmentOutput | null;
}

const SummaryEditState: React.FC<SummaryEditStateProps> = ({
  handleDeleteFile,
  fileLoading,
  isFileLimitOver,
  fileError,
  hasPolingLoadData,
  handleImageUpload,
  appointment,
}) => {
  const { data: locale } = useGetPage({
    locale: 'en',
    pageId: pageIds.COMPLETE_MY_VISIT_SUMMARY_MODAL,
  });

  const { control, setValue } = useFormContext();
  const values = useWatch({ control });
  const [selectedOption, setSelectedOptions] = React.useState(
    () => storage.getData() || 'true',
  );

  const isTranscriptionsRemovedDate = moment(appointment?.end)
    .add(30, 'days')
    .isBefore();

  const [checked, setCheced] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState<boolean>(() =>
    storage.getData() ? !storage.getData() : true,
  );

  const options = [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' },
  ];

  const handleAutoFill = useCallback(() => {
    const { summaryDiagnosis, followupRecommendations } = values;
    setValue(
      'summaryDiagnosis',
      summaryDiagnosis
        ? summaryDiagnosis
        : 'Primary Diagnosis: \nDifferential Diagnoses:',
    );
    setValue(
      'followupRecommendations',
      followupRecommendations
        ? followupRecommendations
        : 'Treatment Plan: \nMedications Prescribed: \nRecommendations/Instructions: \nFollow-Up: \nAdditional Notes: ',
    );
  }, [setValue, values]);

  useEffect(() => {
    if (storage.getData() === 'true') {
      handleAutoFill();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Card
        title={locale?.summaryDetails}
        titleClassName="flex text-h4 text-btn-black font-semibold"
        showSeparator
        defaultExpanded
      >
        <div className="w-full flex flex-col gap-30">
          <div className="w-full">
            <FieldLabel name={'aiNotes'} label={locale?.aiNotes} />
            <div className="relative">
              {!hasPolingLoadData && !values?.aiNotes && <LoadingSummary />}
              {isTranscriptionsRemovedDate && !values?.aiNotes && (
                <Alert
                  type="info-new"
                  desktopItemsAlign="desktop:items-start mt-2.5"
                  customContent={
                    <div className="-mt-1">
                      <p className="text-neutral-800 font-semibold">
                        AI transcript no longer available
                      </p>
                      <p className="text-med-gray-3 font-medium">
                        AI-generated transcripts are temporary and are
                        automatically deleted 30 days after the visit
                      </p>
                    </div>
                  }
                />
              )}
              <FormTextarea
                className={twMerge('min-h-[160px] max-h-[200px]')}
                wrapperClassName={twMerge(
                  isTranscriptionsRemovedDate && 'hidden',
                )}
                name="aiNotes"
                startHeight={160}
                maxLength={MAX_TEXT_AREA_LENGTH}
                placeholder={locale?.enterNotes}
                describe="Generated by Vonage. These AI notes are visible only to you and help summarize key points from the appointment."
              />
            </div>
          </div>

          <div className="w-full">
            <FieldLabel
              name={'appointmentTranscriptions'}
              label={'AI Transcriptions'}
            />
            <div className="relative">
              {!hasPolingLoadData && !values?.appointmentTranscriptions && (
                <LoadingSummary />
              )}
              <FormTextarea
                className="min-h-[160px] max-h-[200px]"
                name="appointmentTranscriptions"
                startHeight={160}
                disabled
                readOnly
                placeholder={locale?.enterNotes}
                describe="Generated by Vonage. These AI notes are visible only to you and help summarize key points from the appointment."
              />
            </div>
          </div>

          <FormTextarea
            className="min-h-[160px] max-h-[200px]"
            name="appointmentNotes"
            startHeight={160}
            label={locale?.youSummaryNotes}
            placeholder={locale?.enterNotes}
            maxLength={MAX_TEXT_AREA_LENGTH}
            describe={locale?.theseNotes}
          />

          <hr />

          <div className="flex justify-between w-full items-start">
            <h3 className="flex text-h4 text-btn-black font-semibold">
              {locale?.pleaseFillOut}
            </h3>

            <div className="text-sm text-clc-red font-bold whitespace-nowrap">
              {locale?.requiredLabel}
            </div>
          </div>

          <FormTextarea
            className="min-h-[160px] max-h-[200px]"
            name="summaryDiagnosis"
            isRequired
            startHeight={160}
            label={locale?.yourSummaryDiagnosis}
            placeholder={locale?.enterNotes}
            maxLength={MAX_TEXT_AREA_LENGTH}
            autoFocus
          />

          <FormTextarea
            className="min-h-[160px] max-h-[200px]"
            name="followupRecommendations"
            isRequired
            startHeight={160}
            label={locale?.followUpRecommendations}
            placeholder={locale?.enterNotes}
            maxLength={MAX_TEXT_AREA_LENGTH}
          />

          <hr />

          <div>
            <div>
              <label className="justify-between mb-0.5 w-full text-base text-btn-black font-semibold">
                {locale?.additionalResourcesAndInformation}
              </label>

              <Dropzone
                label={
                  isFileLimitOver
                    ? locale?.maxFilesLimitDropFile
                    : locale?.dropFileHereToUpload
                }
                supportText={!isFileLimitOver ? locale?.supportedFormat : ''}
                className="col-span-5 mb-5 py-12"
                onDrop={handleImageUpload}
                error={fileError}
                accept={allowedTypes}
                disabled={isFileLimitOver || fileLoading}
                isLoading={fileLoading}
              />
            </div>
            <div className="flex flex-col gap-2.5">
              {((values?.files as FileItemType[]) ?? []).map((file, index) => {
                return (
                  <FileItem
                    file={file}
                    onDeleteFile={() => handleDeleteFile(index)}
                    key={file.fileId}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      <ModalComponent
        title={
          'Would you like to document your post-appointment summary using the SOAP format?'
        }
        modalDesktopWidth="desktop:w-[720px]"
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      >
        <p className="font-medium text-med-gray-3">
          SOAP (Subjective, Objective, Assessment, Plan) provides a structured
          approach to documenting post-appointment summaries.
        </p>
        <p className="text-charcoal-gray font-semibold -mb-5">
          Use SOAP Format:
        </p>
        <RadioGroup
          name={'Use'}
          options={options}
          selectedValue={selectedOption}
          className="flex gap-6"
          onChange={(value) => setSelectedOptions(value as string)}
        />
        <div
          className={twMerge(
            'flex flex-col desktop:flex-row mt-auto desktop:-mx-10 -mx-7 px-7 -mb-10 py-30 rounded-b-10 gap-5 justify-between bg-white desktop:px-10',
          )}
        >
          <Checkbox
            label="Remember my choice and don't show this message again"
            checked={checked}
            isAlternativeColor
            onChange={(v) => setCheced(v)}
          />
          <ButtonComponent
            onClick={() => {
              selectedOption === 'true' && handleAutoFill();
              setIsOpen(false);
              checked && storage.setData(selectedOption);
            }}
            className="!w-fit "
            testID="continue"
          >
            Continue
          </ButtonComponent>
        </div>
      </ModalComponent>
    </>
  );
};

export default SummaryEditState;
